import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import moment from "moment/min/moment-with-locales";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Building2, FileBadge, KeyRound, Loader2, LogOut, Mail, MapPin, Phone, X } from "lucide-react";
import Modal from "@/components/Modal";
import { logout } from "@/services/slices/auth.slice";
import {
  GetBusinessPlanAPI,
  type BusinessPlanResult,
  type BusinessPlanType,
} from "@/services/api/business/getBusinessPlan";
import { CreateCheckoutSessionAPI } from "@/services/api/business/createCheckoutSession";
import {
  CreateSetupIntentAPI,
  GetBillingSummaryAPI,
  type BillingSummaryCard,
  type BillingSummaryResult,
} from "@/services/api/business/plan";
import { CancelBusinessSubscriptionAPI } from "@/services/api/business/cancelBusinessSubscription";
import { ChangeBusinessPlanAPI } from "@/services/api/business/changeBusinessPlan";
import { ChangePasswordAPI } from "@/services/api/user/changePassword";
import { formatMoney } from "@/utils/businessStatement";

const PLAN_TILES: { type: BusinessPlanType; priceKey: "yearly_price"; periodKey: "year" }[] = [
  { type: "yearly", priceKey: "yearly_price", periodKey: "year" },
];

const PASSWORD_MIN_LENGTH = 8;

/**
 * Stripe.js for the embedded Payment Element (the card-update modal): loaded
 * once at module scope, or null until the publishable key is filled in — the
 * modal then refuses to open with a clear "not configured" message instead of
 * asking Stripe for an undefined key.
 */
const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

/**
 * "Yearly plan · HKD 500/year" — the billing panel's plan line: the plan
 * type plus the rate snapshot the summary bills (the wording the dashboard's
 * statement card already uses).
 */
const describePlanPrice = (
  summary: BillingSummaryResult,
  t: TFunction,
): string => {
  const yearly = summary.plan_type === "yearly";
  const fee = yearly ? summary.yearly_fee : summary.monthly_fee;
  const period = t(yearly ? "year" : "month");
  return `${t(yearly ? "Yearly plan" : "Monthly plan")} · HKD ${
    fee ?? (yearly ? 500 : 400)
  }/${period}`;
};

/** Stored card status with its label, falling back to the raw value. */
const describeCardStatus = (
  status: string | null | undefined,
  t: TFunction,
): string => {
  if (status === "valid") return t("Card valid");
  if (status === "invalid") return t("Card invalid");
  if (status === "expired") return t("Card expired");
  if (status === "removed") return t("Card removed");
  return status || "-";
};

/**
 * Business account — the company profile the backend keeps in
 * `business_profiles`. The profile fields are shown read-only:
 * `editAccount/editProfile` writes `profile_icon`/`phone_num`, columns the
 * chat-rooms migration dropped from `users`. Change Password mirrors the
 * student flow and posts to `editAccount/changePassword`, which still writes
 * the dropped `users.password` column — login verifies `authentication.password`,
 * so the endpoint needs a server-side fix before the form can succeed.
 */
const BusinessAccountPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const business = useSelector((state: any) => state.auth.business);
  const [showBrModal, setShowBrModal] = useState(false);

  const [plan, setPlan] = useState<BusinessPlanResult | null>(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [planError, setPlanError] = useState("");
  const [selectedPlan, setSelectedPlan] = useState<BusinessPlanType>("yearly");
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [planBusy, setPlanBusy] = useState(false);
  const [planActionError, setPlanActionError] = useState("");
  const [cardBusy, setCardBusy] = useState(false);
  const [cardInvalid, setCardInvalid] = useState(false);
  const [cardUpdated, setCardUpdated] = useState(false);

  // In-page billing panel (Manage Billing) and the embedded Stripe
  // Payment Element card form (Update card) — both replace the old redirects
  // to Stripe-hosted pages.
  const [billingVisible, setBillingVisible] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingSummary, setBillingSummary] = useState<BillingSummaryResult | null>(null);
  const [cardFormVisible, setCardFormVisible] = useState(false);
  // SetupIntent client secret for the Payment Element; the card data itself
  // goes straight from Stripe.js to Stripe and never enters our state.
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  // Card snapshot as it was when the modal opened, so the post-submit re-read
  // can tell the new card's snapshot apart from the stale one.
  const cardBeforeUpdateRef = useRef<BillingSummaryCard | null>(null);

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordInvalid, setPasswordInvalid] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const [alertVisible, setAlertVisible] = useState(false);
  const [alertText, setAlertText] = useState("");

  // In-app confirm dialog (replaces window.confirm for the cancel-subscription
  // flow): `confirm(message)` opens it and resolves true only when the
  // primary button is pressed; the Cancel button and the backdrop both
  // resolve false. The resolver lives in a ref so no state update is needed
  // to hand it back.
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const confirmResolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = (message: string) =>
    new Promise<boolean>((resolve) => {
      confirmResolveRef.current = resolve;
      setConfirmText(message);
      setConfirmVisible(true);
    });

  const settleConfirm = (value: boolean) => {
    setConfirmVisible(false);
    setConfirmText("");
    const resolve = confirmResolveRef.current;
    confirmResolveRef.current = null;
    resolve?.(value);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/business/login");
  };

  const loadPlan = useCallback(async () => {
    setPlanLoading(true);
    setPlanError("");
    const resp = await GetBusinessPlanAPI();
    if (resp?.status === 0) {
      setPlan(resp.result ?? null);
      setCardInvalid(resp.result?.card_status === "invalid");
      // Keep the selection on whatever is next to be billed, so a queued change
      // does not jump back to the plan still running.
      const nextPlan = resp.result?.pending_plan_type || resp.result?.plan_type;
      if (nextPlan) {
        setSelectedPlan(nextPlan as BusinessPlanType);
      }
    } else if (resp?.status === -40029) {
      setCardInvalid(true);
    } else {
      setPlan(null);
      setPlanError(t("please_try_again"));
    }
    setPlanLoading(false);
  }, [t]);

  useEffect(() => {
    // `?billing=success` comes back from Stripe Checkout; `?card=updated` is
    // the legacy setup-mode landing (the card form is in-page now). Both
    // re-read the plan on landing.
    const params = new URLSearchParams(window.location.search);
    if (params.get("card") === "updated") {
      setCardUpdated(true);
      params.delete("card");
      const query = params.toString();
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${query ? `?${query}` : ""}`,
      );
    }
    loadPlan();
  }, [loadPlan]);

  // Asks the backend for a SetupIntent, then opens the embedded Stripe
  // Payment Element with its client secret. The card data goes straight from
  // Stripe.js to Stripe — this page never sees a PAN. Recovery route: it stays
  // reachable while the card gate is failing, because updating the card is
  // the way out of it.
  const handleUpdateCard = async () => {
    setCheckoutError("");
    if (!stripePromise) {
      // Publishable key not filled in for this environment yet.
      setCheckoutError(t("Payment gateway not configured"));
      return;
    }
    setCardBusy(true);
    const resp = await CreateSetupIntentAPI();
    setCardBusy(false);
    if (resp?.status === 0 && resp.result?.client_secret) {
      cardBeforeUpdateRef.current = billingSummary?.card ?? null;
      setClientSecret(resp.result.client_secret);
      setCardFormVisible(true);
      return;
    }
    setCheckoutError(String(resp?.result?.message ?? "") || t("please_try_again"));
  };

  // Once Stripe.js confirms the SetupIntent, the setup_intent.succeeded
  // webhook applies the card (subscription default, the customer-level
  // invoice_settings default that shortfall/late fees charge, and the local
  // snapshot), which can lag a beat behind the confirmation. Re-read the
  // summary a few times until that snapshot lands; the "Card updated" notice
  // shows either way, and the last read wins.
  const refreshAfterCardUpdate = async () => {
    setCardFormVisible(false);
    setClientSecret(null);
    setCardUpdated(true);

    const before = cardBeforeUpdateRef.current;
    let summary: BillingSummaryResult | null = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const resp = await GetBillingSummaryAPI();
      if (resp?.status === 0 && resp.result) summary = resp.result;
      const card = summary?.card ?? null;
      const newCardShown =
        Boolean(card) &&
        (!before ||
          card?.last4 !== before.last4 ||
          card?.exp_month !== before.exp_month ||
          card?.exp_year !== before.exp_year);
      if (newCardShown) break;
      if (attempt < 2) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }
    if (summary) setBillingSummary(summary);
    // The banners up top mirror the plan's card gate, so re-read the plan.
    await loadPlan();
  };

  const handleChoosePlan = async () => {
    setCheckoutBusy(true);
    setCheckoutError("");
    const resp = await CreateCheckoutSessionAPI({ plan_type: selectedPlan });
    if (resp?.status === 0 && resp.result?.url) {
      window.location.href = resp.result.url;
      return;
    }
    setCheckoutError(t("please_try_again"));
    setCheckoutBusy(false);
  };

  // Manage Billing now opens the in-page billing panel fed by billingSummary;
  // the old flow handed the business to the Stripe Billing Portal off-site.
  const handleManageBilling = async () => {
    setBillingVisible(true);
    setCheckoutBusy(true);
    setCheckoutError("");
    setBillingLoading(true);
    const resp = await GetBillingSummaryAPI();
    if (resp?.status === 0) {
      setBillingSummary(resp.result ?? null);
    } else {
      setBillingSummary(null);
      setCheckoutError(t("please_try_again"));
    }
    setBillingLoading(false);
    setCheckoutBusy(false);
  };

  const handleCancelSubscription = async (cancel: boolean) => {
    if (cancel) {
      // Before the 12-month minimum term a cancellation stops the plan at the
      // term end, so the confirmation names that date instead.
      const beforeTerm =
        Boolean(plan?.minimum_term_end) &&
        moment(plan?.minimum_term_end).isAfter(moment());
      const message = beforeTerm
        ? t(
            "Cancelling will stop your plan at the end of your 12-month minimum term ({{date}}). Continue?",
            { date: moment(plan?.minimum_term_end).format("DD MMM YYYY") },
          )
        : t("Your plan stays active until {{date}}. Continue?", {
            date: plan?.renews_on
              ? moment(plan.renews_on).format("DD MMM YYYY")
              : t("the end of the current period"),
          });
      const ok = await confirm(message);
      if (!ok) return;
    }
    setPlanBusy(true);
    setPlanActionError("");
    const resp = await CancelBusinessSubscriptionAPI({ cancel });
    if (resp?.status === 0) {
      await loadPlan();
    } else {
      setPlanActionError(t("please_try_again"));
    }
    setPlanBusy(false);
  };

  // Subscribed businesses keep the current period, so the change only queues
  // and the API promotes it on the next renewal.
  const handleChangePlan = async () => {
    setPlanBusy(true);
    setPlanActionError("");
    const resp = await ChangeBusinessPlanAPI({ plan_type: selectedPlan });
    if (resp?.status === 0) {
      await loadPlan();
    } else {
      setPlanActionError(t("please_try_again"));
    }
    setPlanBusy(false);
  };

  const showAlert = (text: string) => {
    setAlertText(text);
    setAlertVisible(true);
  };

  const openChangePassword = () => {
    setOldPassword("");
    setNewPassword("");
    setPasswordInvalid(false);
    setPasswordVisible(true);
  };

  const savePassword = async () => {
    setPasswordInvalid(true);
    if (!oldPassword || !newPassword) return;
    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      return showAlert(t("Invalid New Password"));
    }

    setSavingPassword(true);
    const resp = await ChangePasswordAPI(newPassword);
    setSavingPassword(false);

    if (resp?.status !== 0) {
      return showAlert(t("Can`t Change Password"));
    }

    setPasswordVisible(false);
    showAlert(t("Password changed successfully!"));
  };

  const hasSubscription = plan?.status === "active" || plan?.status === "past_due";
  // A canceled or locked account cannot use paid features — and "Choose Plan"
  // can only come back as -40032 — so the page says so and hides that action.
  const terminated =
    plan?.status === "canceled" || plan?.access_status === "locked";
  // A queued change keeps the stored plan_type running until it is promoted, so
  // the tiles compare against whichever is actually next to be billed.
  const effectivePlan = (plan?.pending_plan_type || plan?.plan_type || "yearly") as BusinessPlanType;
  const canChangePlan = hasSubscription && selectedPlan !== effectivePlan;
  const cancelScheduled = Boolean(plan?.cancel_at || plan?.cancel_at_period_end);
  const logo = business?.icon || "";

  const summary = billingSummary;
  const outstanding = summary?.outstanding;

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-8">
      <h1 className="text-2xl font-bold text-[#333]">{t("Account")}</h1>

      <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-col items-center gap-4 border-b border-gray-100 bg-[#fffaf2] px-6 py-8 sm:flex-row">
          <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-sm">
            {logo ? (
              <img src={logo} alt={business?.company_name || t("Company Name")} className="h-full w-full object-contain" />
            ) : (
              <Building2 size={30} className="text-[#ffa217]" />
            )}
          </div>
          <div className="min-w-0 text-center sm:text-left">
            <h2 className="truncate text-xl font-bold text-gray-900">
              {business?.company_name || t("Company Name")}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {business?.company_email || ""}
            </p>
            {business?.ctime ? (
              <p className="mt-1 text-xs text-gray-400">
                {t("Member Since")}: {moment(business.ctime).format("DD MMM YYYY")}
              </p>
            ) : null}
          </div>
        </div>

        <dl className="grid grid-cols-1 gap-px bg-gray-50 sm:grid-cols-2">
          <DetailRow
            icon={<Building2 size={16} />}
            label={t("Company Name")}
            value={business?.company_name}
          />
          <DetailRow
            icon={<Mail size={16} />}
            label={t("Company Email")}
            value={business?.company_email}
          />
          <DetailRow
            icon={<Phone size={16} />}
            label={t("Contact Number")}
            value={business?.contact_number}
          />
          <DetailRow
            icon={<MapPin size={16} />}
            label={t("Company Address")}
            value={business?.company_address}
          />
          <DetailRow
            icon={<FileBadge size={16} />}
            label={t("BR Number")}
            value={business?.br_number}
          />
          <DetailRow
            icon={<FileBadge size={16} />}
            label={t("BR Document")}
            value={
              business?.br_image ? (
                <button
                  type="button"
                  onClick={() => setShowBrModal(true)}
                  className="font-semibold text-[#ffa217] underline"
                >
                  {t("View")}
                </button>
              ) : null
            }
          />
        </dl>
      </section>

      <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <h2 className="text-base font-bold text-gray-900">{t("Billing & Plan")}</h2>

        {planLoading ? (
          <p className="mt-3 text-sm text-gray-400">{t("loading")}</p>
        ) : planError ? (
          <p className="mt-3 text-sm text-red-500">{planError}</p>
        ) : (
          <>
            {/*
              Account status banner — states the current situation and the
              concrete next step. First matching state wins, highest impact
              first; the last case (an ordinary active plan) needs no banner.
            */}
            {terminated ? (
              <div className="mb-4 rounded-xl border border-red-200 bg-[#ffebee] px-4 py-3">
                <p className="text-sm font-semibold text-red-700">
                  {t(
                    "Your subscription has ended. Paid features are disabled until the account is reactivated.",
                  )}
                </p>
                <p className="mt-1 text-sm text-red-700">
                  {t("Contact support to reactivate your account.")}
                </p>
              </div>
            ) : plan?.overdue?.has_overdue ? (
              <div className="mb-4 rounded-xl border border-amber-200 bg-[#fff3e0] px-4 py-3">
                <p className="text-sm font-semibold text-[#b26a00]">
                  {t("Payment overdue")}
                </p>
                <p className="mt-1 text-sm text-[#b26a00]">
                  {t(
                    "A late fee of HKD {{amount}} has accrued over {{days}} day(s).",
                    {
                      amount: plan.overdue.accrued_late_fee_hkd,
                      days: plan.overdue.days_charged,
                    },
                  )}
                </p>
                <p className="mt-1 text-sm text-[#b26a00]">
                  {t(
                    "Update your card or pay the outstanding amount to avoid losing access.",
                  )}
                </p>
                <button
                  type="button"
                  onClick={handleManageBilling}
                  disabled={checkoutBusy}
                  className="mt-2 inline-flex items-center gap-2 rounded-xl bg-[#ffa217] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#e89614] disabled:opacity-50"
                >
                  {t("Pay now")}
                </button>
              </div>
            ) : cardInvalid ? (
              // Points at the detailed "Update card" block further down rather
              // than repeating its wording and button.
              <div className="mb-4 rounded-xl border border-red-200 bg-[#ffebee] px-4 py-3">
                <p className="text-sm font-semibold text-red-700">
                  {t("Your card on file is no longer valid.")}
                </p>
                <p className="mt-1 text-sm text-red-700">
                  {t(
                    "Use the Update card button below to update your payment method.",
                  )}
                </p>
              </div>
            ) : plan?.status === "past_due" ? (
              <div className="mb-4 rounded-xl border border-amber-200 bg-[#fff3e0] px-4 py-3">
                <p className="text-sm font-semibold text-[#b26a00]">
                  {t("Your last payment failed.")}
                </p>
                <p className="mt-1 text-sm text-[#b26a00]">
                  {t("Update your card to complete the payment.")}
                </p>
              </div>
            ) : cancelScheduled ? (
              <div className="mb-4 rounded-xl border border-amber-200 bg-[#fff3e0] px-4 py-3">
                <p className="text-sm font-semibold text-[#b26a00]">
                  {t("Auto-renewal is off. Your plan stays active until {{date}}.", {
                    date: plan?.cancel_at
                      ? moment(plan.cancel_at).format("DD MMM YYYY")
                      : plan?.renews_on
                        ? moment(plan.renews_on).format("DD MMM YYYY")
                        : t("the end of the current period"),
                  })}
                </p>
                <p className="mt-1 text-sm text-[#b26a00]">
                  {t("Re-enable auto-renewal")}
                </p>
              </div>
            ) : plan?.is_promo && !hasSubscription ? (
              <div className="mb-4 rounded-xl border border-amber-200 bg-[#fff3e0] px-4 py-3">
                <p className="text-sm font-semibold text-[#b26a00]">
                  {t("First 100: your first year is free until {{date}}.", {
                    date: plan?.free_trial_end
                      ? moment(plan.free_trial_end).format("DD MMM YYYY")
                      : "-",
                  })}
                </p>
                <p className="mt-1 text-sm text-[#b26a00]">
                  {t("Choose a plan to activate your account.")}
                </p>
              </div>
            ) : null}

            {cardInvalid ? (
              <div className="mb-3 rounded-xl bg-red-50 px-4 py-3">
                <p className="text-sm font-medium text-red-600">
                  {t("Your payment method is invalid. Please update your card.")}
                </p>
                <button
                  type="button"
                  onClick={handleUpdateCard}
                  disabled={cardBusy}
                  className="mt-2 inline-flex items-center gap-2 rounded-xl bg-[#ffa217] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#e89614] disabled:opacity-50"
                >
                  {t("Update card")}
                </button>
              </div>
            ) : null}

            {cardUpdated ? (
              <p className="mb-3 rounded-xl bg-green-50 px-4 py-3 text-sm font-medium text-green-700">
                {t("Card updated")}
              </p>
            ) : null}

            {/* The promo lines promise a free future year, which is misleading
                once the account is terminated — hide them in that state. */}
            {!terminated && plan?.is_promo && plan?.free_trial_end ? (
              <>
                <p className="mt-1 text-sm text-[#ffa217]">
                  {t("Free until {{date}}", {
                    date: moment(plan.free_trial_end).format("DD MMM YYYY"),
                  })}
                </p>
                {plan?.is_promo_first_100 ? (
                  <p className="mt-1 text-xs text-gray-400">
                    {t("First 100 businesses: first year free")}
                  </p>
                ) : null}
              </>
            ) : null}

            <p className="mt-2 text-sm text-gray-600">
              <span className="font-semibold text-gray-900">
                {t("Current Plan")}:{" "}
              </span>
              {hasSubscription ? (
                <>
                  {plan?.plan_type === "yearly" ? t("Yearly") : t("Monthly")}
                  {plan?.renews_on
                    ? ` · ${t("Renews on {{date}}", {
                        date: moment(plan.renews_on).format("DD MMM YYYY"),
                      })}`
                    : ""}
                </>
              ) : plan?.is_promo ? (
                // The promo business is on the free first year — calling it
                // "No active plan" contradicted the "Free until" line above.
                t("Free plan (First 100)")
              ) : (
                t("No active plan")
              )}
            </p>

            {plan?.minimum_term_end ? (
              <p className="mt-1 text-xs text-gray-400">
                {t("Minimum term ends {{date}}", {
                  date: moment(plan.minimum_term_end).format("DD MMM YYYY"),
                })}
              </p>
            ) : null}

            {cancelScheduled ? (
              <p className="mt-1 text-sm text-red-500">
                {t("Subscription ends on {{date}}", {
                  date: plan?.cancel_at
                    ? moment(plan.cancel_at).format("DD MMM YYYY")
                    : plan?.renews_on
                      ? moment(plan.renews_on).format("DD MMM YYYY")
                      : t("the end of the current period"),
                })}
              </p>
            ) : null}

            {/* Same contradiction as the promo lines: a terminated account is
                told to "complete" a subscription it cannot buy. */}
            {!hasSubscription && !plan?.is_promo && !terminated ? (
              <p className="mt-3 rounded-xl bg-[#fff3e0] px-4 py-3 text-sm font-medium text-[#b26a00]">
                {t("Your registration is approved. Complete your subscription to activate your account.")}
              </p>
            ) : null}

            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {PLAN_TILES.map((tile) => {
                const selected = selectedPlan === tile.type;
                // A queued plan is not billed yet, so it gets a badge instead of
                // the "current plan" treatment.
                const queued = plan?.pending_plan_type === tile.type;
                const feeKey = "yearly_fee";
                return (
                  <button
                    key={tile.type}
                    type="button"
                    onClick={() => setSelectedPlan(tile.type)}
                    className={`rounded-xl border p-4 text-left transition ${
                      selected
                        ? "border-[#ffa217] ring-2 ring-[#ffa217]/30"
                        : "border-gray-200 hover:border-[#ffa217]/60"
                    }`}
                  >
                    <span className="block text-sm font-bold text-gray-900">
                      {tile.type === "yearly" ? t("Yearly") : t("Monthly")}
                    </span>
                    <span className="mt-1 block text-sm text-gray-500">
                      HKD {plan?.[tile.priceKey] ?? plan?.[feeKey] ?? 500}
                      /{t(tile.periodKey)}
                    </span>
                    {queued ? (
                      <span className="mt-2 inline-block rounded-full bg-[#fff3e0] px-2 py-0.5 text-xs font-semibold text-[#ffa217]">
                        {tile.type === "yearly" ? t("Yearly") : t("Monthly")} {t("from next renewal")}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            {checkoutError || planActionError ? (
              <p className="mt-3 text-sm text-red-500">{checkoutError || planActionError}</p>
            ) : null}

            {canChangePlan ? (
              <button
                type="button"
                onClick={handleChangePlan}
                disabled={planBusy}
                className="mt-4 inline-flex items-center gap-2 rounded-xl border border-[#ffa217] px-4 py-2 text-sm font-semibold text-[#ffa217] transition hover:bg-[#fffaf2] disabled:opacity-50"
              >
                {t("Change Plan at Next Renewal")}
              </button>
            ) : null}

            {hasSubscription ? (
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleManageBilling}
                  disabled={checkoutBusy}
                  className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217] disabled:opacity-50"
                >
                  {t("Manage Billing")}
                </button>
                <button
                  type="button"
                  onClick={handleUpdateCard}
                  disabled={cardBusy}
                  className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217] disabled:opacity-50"
                >
                  {t("Update card")}
                </button>
                <button
                  type="button"
                  onClick={() => handleCancelSubscription(!cancelScheduled)}
                  disabled={planBusy}
                  className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:border-red-300 hover:text-red-500 disabled:opacity-50"
                >
                  {cancelScheduled ? t("Re-enable auto-renewal") : t("Cancel Subscription")}
                </button>
              </div>
            ) : terminated ? (
              // Checkout only answers -40032 while the account is terminated,
              // so the button is hidden; the banner above points at support.
              null
            ) : (
              <button
                type="button"
                onClick={handleChoosePlan}
                disabled={checkoutBusy}
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#ffa217] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#e89614] disabled:opacity-50"
              >
                {t("Choose Plan")}
              </button>
            )}
          </>
        )}
      </section>

      <section className="flex items-center justify-between rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <p className="text-sm font-semibold text-gray-900">{t("Change Password")}</p>
        <button
          type="button"
          onClick={openChangePassword}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
        >
          <KeyRound size={15} />
          {t("Change Password")}
        </button>
      </section>

      <section className="flex items-center justify-between rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <p className="text-sm font-semibold text-gray-900">{t("Logout")}</p>
        <button
          type="button"
          onClick={handleLogout}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
        >
          <LogOut size={15} />
          {t("Logout")}
        </button>
      </section>

      <Modal visible={showBrModal} onClose={() => setShowBrModal(false)}>
        <div className="flex max-h-[80vh] flex-col gap-3 overflow-hidden rounded-2xl bg-white p-5 shadow-lg">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-gray-900">{t("BR Document")}</h3>
            <button
              type="button"
              onClick={() => setShowBrModal(false)}
              aria-label={t("Close")}
              className="rounded-full p-1 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
            >
              <X size={18} className="text-gray-500" />
            </button>
          </div>
          {business?.br_image ? (
            <img
              src={business.br_image}
              alt={business.company_name || t("BR Document")}
              className="max-h-[62vh] w-full rounded-xl object-contain"
            />
          ) : null}
        </div>
      </Modal>

      {/* Change password */}
      <Modal visible={passwordVisible} onClose={() => setPasswordVisible(false)}>
        <div className="rounded-2xl bg-white p-5 shadow-lg">
          <div className="mb-4 text-center text-lg font-bold text-gray-900">
            {t("Change Password")}
          </div>

          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                {t("Old Password")}
              </label>
              <input
                type="password"
                value={oldPassword}
                onChange={(e) => {
                  setOldPassword(e.target.value);
                  setPasswordInvalid(false);
                }}
                className={`w-full rounded-xl border bg-[#fff3e0]/50 py-2.5 px-3.5 text-sm text-gray-900 transition-all focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#ffa217]/30 ${
                  passwordInvalid && !oldPassword
                    ? "border-red-400"
                    : "border-gray-200 focus:border-[#ffa217]/50"
                }`}
                placeholder="••••••••"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                {t("New Password")}
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setPasswordInvalid(false);
                }}
                className={`w-full rounded-xl border bg-[#fff3e0]/50 py-2.5 px-3.5 text-sm text-gray-900 transition-all focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#ffa217]/30 ${
                  passwordInvalid && !newPassword
                    ? "border-red-400"
                    : "border-gray-200 focus:border-[#ffa217]/50"
                }`}
                placeholder="••••••••"
              />
            </div>
          </div>

          <div className="mt-5 flex gap-3">
            <button
              type="button"
              onClick={() => setPasswordVisible(false)}
              className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={savePassword}
              disabled={savingPassword}
              className="flex-1 rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white transition hover:bg-[#e08f0e] disabled:opacity-60"
            >
              {savingPassword ? (
                <Loader2 size={16} className="mx-auto animate-spin" />
              ) : (
                t("Save")
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Generic alert */}
      <Modal visible={alertVisible} onClose={() => setAlertVisible(false)}>
        <div className="rounded-2xl bg-white p-6 text-center shadow-lg">
          <div className="mb-4 text-lg font-semibold text-gray-900">{alertText}</div>
          <button
            type="button"
            onClick={() => setAlertVisible(false)}
            className="rounded-lg bg-[#ffa217] px-6 py-2.5 font-semibold text-white transition hover:opacity-90"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>

      {/* Two-button confirm — replaces window.confirm for cancelling the
          subscription. Backdrop click (Modal onClose) = cancel. */}
      <Modal visible={confirmVisible} onClose={() => settleConfirm(false)}>
        <div className="rounded-2xl bg-white p-6 text-center shadow-lg">
          <div className="mb-4 text-lg font-semibold text-gray-900">{confirmText}</div>
          <div className="flex justify-center gap-3">
            <button
              type="button"
              onClick={() => settleConfirm(false)}
              className="rounded-xl border border-gray-200 px-6 py-2.5 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={() => settleConfirm(true)}
              className="rounded-lg bg-[#ffa217] px-6 py-2.5 font-semibold text-white transition hover:opacity-90"
            >
              {t("Continue")}
            </button>
          </div>
        </div>
      </Modal>

      {/*
        Billing panel — "Manage Billing" opens this instead of handing the
        business to the Stripe Billing Portal. Everything it shows comes from
        GET /api/business/plan/billingSummary.
      */}
      <Modal visible={billingVisible} onClose={() => setBillingVisible(false)}>
        <div className="max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-5 shadow-lg">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-gray-900">
              {t("Manage Billing")}
            </h3>
            <button
              type="button"
              onClick={() => setBillingVisible(false)}
              aria-label={t("Close")}
              className="rounded-full p-1 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
            >
              <X size={18} className="text-gray-500" />
            </button>
          </div>

          {billingLoading ? (
            <p className="mt-4 text-sm text-gray-400">{t("loading")}</p>
          ) : !summary ? (
            <p className="mt-4 text-sm text-red-500">
              {checkoutError || t("please_try_again")}
            </p>
          ) : (
            <div className="mt-4 space-y-4">
              {checkoutError ? (
                <p className="text-sm text-red-500">{checkoutError}</p>
              ) : null}

              {/* The confirmation stays visible while the panel is open. */}
              {cardUpdated ? (
                <p className="rounded-xl bg-green-50 px-4 py-3 text-sm font-medium text-green-700">
                  {t("Card updated")}
                </p>
              ) : null}

              {/* Plan line plus the same renewal / term / free-trial dates the
                  billing section derives for this account. */}
              <div className="rounded-xl bg-[#fffaf2] px-4 py-3">
                <p className="text-sm font-bold text-gray-900">
                  {describePlanPrice(summary, t)}
                </p>
                {summary.is_promo && summary.free_trial_end ? (
                  <p className="mt-1 text-xs text-[#ffa217]">
                    {t("Free until {{date}}", {
                      date: moment(summary.free_trial_end).format("DD MMM YYYY"),
                    })}
                  </p>
                ) : summary.renews_on ? (
                  <p className="mt-1 text-xs text-gray-500">
                    {t("Renews on {{date}}", {
                      date: moment(summary.renews_on).format("DD MMM YYYY"),
                    })}
                  </p>
                ) : null}
                {summary.minimum_term_end ? (
                  <p className="mt-1 text-xs text-gray-400">
                    {t("Minimum term ends {{date}}", {
                      date: moment(summary.minimum_term_end).format("DD MMM YYYY"),
                    })}
                  </p>
                ) : null}
              </div>

              {/* Outstanding balance — only a real balance earns a breakdown. */}
              <div className="rounded-xl border border-gray-100 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {t("Outstanding balance")}
                </p>
                {outstanding?.has_outstanding ? (
                  <>
                    <p className="mt-1 text-2xl font-bold text-gray-900">
                      {formatMoney(outstanding.total_hkd ?? 0, "HKD")}
                    </p>
                    <ul className="mt-2 space-y-1">
                      <li className="flex items-center justify-between text-sm text-gray-600">
                        <span>{t("Late fee")}</span>
                        <span>
                          {formatMoney(outstanding.late_fee_hkd ?? 0, "HKD")}
                        </span>
                      </li>
                      {(outstanding.shortfall_count ?? 0) > 0 ||
                      (outstanding.shortfall_hkd ?? 0) > 0 ? (
                        <li className="flex items-center justify-between text-sm text-gray-600">
                          <span>
                            {t("Shortfall")}
                            {(outstanding.shortfall_count ?? 0) > 0
                              ? ` × ${outstanding.shortfall_count}`
                              : ""}
                          </span>
                          <span>
                            {formatMoney(outstanding.shortfall_hkd ?? 0, "HKD")}
                          </span>
                        </li>
                      ) : null}
                    </ul>
                  </>
                ) : (
                  <p className="mt-1 text-sm font-medium text-gray-600">
                    {t("Nothing outstanding")}
                  </p>
                )}
              </div>

              {/* Card on file — the Stripe-derived snapshot only. */}
              <div className="rounded-xl border border-gray-100 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {t("Card on file")}
                </p>
                {summary.card ? (
                  <>
                    <p className="mt-1 text-sm font-semibold text-gray-900">
                      {summary.card.brand} •••• {summary.card.last4}
                      {summary.card.exp_month && summary.card.exp_year
                        ? ` (${t("Expiry")} ${String(summary.card.exp_month).padStart(2, "0")}/${String(
                            summary.card.exp_year,
                          ).slice(-2)})`
                        : ""}
                    </p>
                    <p className="mt-1 text-xs text-gray-400">
                      {t("Status")}:{" "}
                      {describeCardStatus(
                        summary.card.status ?? summary.card_status,
                        t,
                      )}
                    </p>
                  </>
                ) : (
                  <p className="mt-1 text-sm font-medium text-gray-600">
                    {t("No card on file")}
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={handleUpdateCard}
                disabled={cardBusy}
                className="inline-flex items-center gap-2 rounded-xl bg-[#ffa217] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#e89614] disabled:opacity-50"
              >
                {t("Update card")}
              </button>

              {/*
                Month-by-month fee detail already lives on the dashboard's
                statement view — point there rather than rebuilding a month
                browser inside this panel.
              */}
              <div className="rounded-xl bg-gray-50 px-4 py-3">
                <p className="text-xs text-gray-500">
                  {t("See the Dashboard for the month-by-month statement.")}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setBillingVisible(false);
                    navigate("/portal/dashboard");
                  }}
                  className="mt-2 inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
                >
                  {t("Dashboard")}
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/*
        Update card — Stripe's embedded Payment Element inside the site's own
        modal. The card data goes straight from Stripe.js to Stripe, so no raw
        PAN ever reaches this server (the raw-card POST /updateCard route is
        deliberately gone). The element validates its own fields, so the only
        error line here is the checkoutError slot: Stripe.js / the backend
        message for anything that fails.
      */}
      <Modal visible={cardFormVisible} onClose={() => setCardFormVisible(false)}>
        <div className="max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-5 shadow-lg">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-gray-900">
              {t("Update card")}
            </h3>
            <button
              type="button"
              onClick={() => setCardFormVisible(false)}
              aria-label={t("Close")}
              className="rounded-full p-1 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
            >
              <X size={18} className="text-gray-500" />
            </button>
          </div>

          {checkoutError ? (
            <p className="mt-3 text-sm text-red-500">{checkoutError}</p>
          ) : null}

          {/* The element is mounted only while a fresh client secret is on
              file, so every open of the modal confirms one new SetupIntent. */}
          {clientSecret ? (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <CardUpdateForm
                submitting={cardBusy}
                onStart={() => {
                  setCardBusy(true);
                  setCheckoutError("");
                }}
                onDone={() => setCardBusy(false)}
                onCancel={() => {
                  setCardFormVisible(false);
                  setClientSecret(null);
                }}
                onError={(message) => setCheckoutError(message)}
                onSuccess={refreshAfterCardUpdate}
              />
            </Elements>
          ) : null}
        </div>
      </Modal>
    </div>
  );
};

/**
 * The Payment Element and its buttons. It has to be rendered inside
 * <Elements>, so it lives as its own component; the page owns every state
 * transition (busy flag, error line, close + refresh) through the callbacks.
 */
const CardUpdateForm: React.FC<{
  submitting: boolean;
  onStart: () => void;
  onDone: () => void;
  onCancel: () => void;
  onError: (message: string) => void;
  onSuccess: () => Promise<void>;
}> = ({ submitting, onStart, onDone, onCancel, onError, onSuccess }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();

  const submit = async () => {
    if (submitting || !stripe || !elements) return;
    onStart();
    // Validate/finalise the element first so an incomplete field is reported
    // before anything is sent to Stripe, then confirm the SetupIntent the
    // modal was opened with — no redirect for the plain card flow.
    const submitted = await elements.submit();
    if (submitted.error) {
      onError(submitted.error.message || t("please_try_again"));
      onDone();
      return;
    }
    const { error } = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
    });
    if (error) {
      onError(error.message || t("please_try_again"));
      onDone();
      return;
    }
    await onSuccess();
    onDone();
  };

  return (
    <>
      <div className="mt-4">
        <PaymentElement />
      </div>

      <div className="mt-5 flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217] disabled:opacity-50"
        >
          {t("Cancel")}
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting || !stripe || !elements}
          className="flex-1 rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white transition hover:bg-[#e08f0e] disabled:opacity-60"
        >
          {submitting ? (
            <Loader2 size={16} className="mx-auto animate-spin" />
          ) : (
            t("Update card")
          )}
        </button>
      </div>
    </>
  );
};

const DetailRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value?: React.ReactNode;
}> = ({ icon, label, value }) => (
  <div className="flex items-start gap-3 bg-white px-6 py-4">
    <span className="mt-0.5 text-[#ffa217]">{icon}</span>
    <div className="min-w-0">
      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">
        {label}
      </dt>
      <dd className="mt-1 break-words text-sm font-semibold text-gray-800">
        {value || "-"}
      </dd>
    </div>
  </div>
);

export default BusinessAccountPage;
